/**
 * Inquiries — everything that has come in, newest first (#85).
 *
 * The row is the arrival, not the call and not the Inquiry. A Phone Call
 * and the Inquiry it produced are two rows in the database and one thing
 * from the Company's side, so a call that became a job is one row here
 * rather than two, and a form submission nobody ever rang about is a row
 * like any other. Most calls become no Inquiry at all — that is the
 * ordinary case and it is why this list exists: the only thing that
 * currently leaves the service is the New Inquiry email, and it is only
 * ever sent about the minority that drafted.
 *
 * Rows about one job are one row (#115). The Inquiry that drafted a
 * job and every call whose caller picked it share a Backend Ref, and
 * the server folds them: the row is the Inquiry, named for the job,
 * with how many calls sit under it and when the last one rang, and it
 * opens on the job page where the whole thread is read. A row about no
 * job — a wrong number, a form that failed to draft — is the row it
 * always was and opens on itself. They were drawn as a head with its
 * calls tucked under it for a day; a list where the gala is thirteen
 * rows is a list where the gala is hard to find.
 *
 * It is a log and not a queue. Nothing here is unread, assigned, claimed
 * or dismissed. The one action is delete, and it is the
 * RMS's events-list delete brought across exactly: a checkbox on the
 * row, Delete (N) in the filter row, one confirm. A Company owns what
 * came in and may throw it away, and a log nobody can prune fills with
 * wrong numbers. Deleting a job row deletes what the row is — every
 * call about the job and the Inquiry that drafted it; the job at the
 * rental system is untouched. What an arrival became
 * is not a column: the row is what came in, and what it turned into is
 * read on the arrival itself. Failures are a value of the outcome filter
 * rather than a destination of their own, because a failed draft is one
 * of the things that happened to what came in, not a different subject.
 *
 * The filters are the navigation, so all four live in the URL and commit
 * with `replace` (DESIGN.md, Admin list pages): a Company looking at
 * last week's voicemails is in a place it can link to, come back to, and
 * reach with Back. Search is the one that earns the list its keep — what
 * a Company wants out of a log is rarely the newest arrival, it is the
 * one where somebody said a name, a venue or a number.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { AdminPageHeader } from "../components/ui/AdminPageHeader";
import { Banner } from "../components/ui/Banner";
import { Button } from "../components/ui/Button";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { DateInputNZ } from "../components/ui/DateInputNZ";
import { ChevronLeftIcon, ChevronRightIcon } from "../components/ui/icons";
import {
  ResponsiveTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui/ResponsiveTable";
import { Select } from "../components/ui/Select";
import {
  deleteArrivals,
  listArrivals,
  type ListRow,
} from "../lib/arrivals-api";
import {
  ARRIVED_OPTIONS,
  BECAME_OPTIONS,
  arrivedBadgeClass,
  arrivedLabel,
  callsLine,
  formatEventDate,
  formatWhen,
  whoLabel,
} from "../lib/arrivals-format";
import { errorMessage } from "../lib/error-message";

import styles from "./inquiries.module.css";

const PAGE_SIZE = 25;

/**
 * How long the box waits before it searches.
 *
 * The search runs a scan over every Transcript this Company has, so it is
 * not a thing to do per keystroke — and a list redrawing under the hands
 * of somebody halfway through typing a name is one they cannot read
 * anyway.
 */
const SEARCH_SETTLE_MS = 300;

/**
 * One key per row. A job row is keyed on the job, because that is what
 * the row is and what deleting it deletes; a loose row on its kind and
 * id, since a call and a form submission can share a uuid's shape.
 */
const rowKey = (a: Pick<ListRow, "kind" | "id" | "backend_ref">) =>
  a.backend_ref ? `job:${a.backend_ref}` : `${a.kind}:${a.id}`;

/** Where a row opens: the job it is about, or itself. */
const rowPath = (a: Pick<ListRow, "kind" | "id" | "backend_ref">) =>
  a.backend_ref
    ? `/enquiries/${encodeURIComponent(a.backend_ref)}`
    : `/enquiries/${a.kind}/${a.id}`;

export default function InquiriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { search: query } = useLocation();

  const q = searchParams.get("q") ?? "";
  const arrived = searchParams.get("arrived") ?? "";
  const became = searchParams.get("became") ?? "";
  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const offset = (page - 1) * PAGE_SIZE;

  // The search box keeps a local draft so typing can settle before it
  // becomes a place; every other filter commits as it changes.
  const [typed, setTyped] = useState(q);
  const [arrivals, setArrivals] = useState<ListRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Bumped after a delete so the list reads again from the same place.
  const [reloads, setReloads] = useState(0);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Enquiries | HireOS";
  }, []);

  /**
   * Commit a filter to the URL. Any filter change drops back to page 1 —
   * page 6 of the old results is not a place in the new ones — and
   * `replace` keeps a tweak out of the Back history, so Back leaves the
   * list rather than walking a search back one letter at a time.
   */
  const setFilter = useCallback(
    (patch: Record<string, string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === "") next.delete(key);
            else next.set(key, value);
          }
          if (!("page" in patch)) next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (typed.trim() === q) return;
    const settle = setTimeout(
      () => setFilter({ q: typed.trim() }),
      SEARCH_SETTLE_MS,
    );
    return () => clearTimeout(settle);
  }, [typed, q, setFilter]);

  useEffect(() => {
    const ac = new AbortController();
    setArrivals(null);
    // A selection is of rows on this page; a different page, or the same
    // one read again, is a different set of rows.
    setSelected(new Set());
    listArrivals(offset, PAGE_SIZE, { q, arrived, became, from, to }, ac.signal)
      .then((next) => {
        setArrivals(next.arrivals);
        setTotal(next.total);
        setLoadError(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setLoadError(errorMessage(err));
      });
    return () => ac.abort();
  }, [offset, q, arrived, became, from, to, reloads]);

  const toggleSelected = useCallback((key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleConfirmDelete = async () => {
    const picked = (arrivals ?? []).filter((a) => selected.has(rowKey(a)));
    const refs = picked
      .filter((a) => !a.backend_ref)
      .map(({ kind, id }) => ({ kind, id }));
    const jobs = picked.filter((a) => a.backend_ref).map((a) => a.backend_ref);
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteArrivals(refs, jobs);
      setDeleteOpen(false);
      setReloads((n) => n + 1);
    } catch (err) {
      setDeleteError(errorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const shown = arrivals?.length ?? 0;
  const first = shown === 0 ? 0 : offset + 1;
  const last = offset + shown;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtered = Boolean(q || arrived || became || from || to);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <AdminPageHeader
          title="Enquiries"
          subtitle="Every call and every form your company has taken, and what came of it."
        />

        {loadError && (
          <Banner
            variant="error"
            className={styles.loadBanner}
            onDismiss={() => setLoadError(null)}
          >
            {loadError}
          </Banner>
        )}

        <div className={styles.filters}>
          {/* A search field, which is the one input DESIGN.md lets carry
              a placeholder: it has no label above it and nothing else on
              the row to say what it searches. */}
          <input
            type="search"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Search what was said, a name, or a number"
            aria-label="Search what came in"
            className={styles.searchField}
          />
          <div className={styles.filterSelect}>
            <Select
              size="sm"
              value={arrived}
              onChange={(next) => setFilter({ arrived: next })}
              ariaLabel="Filter by how it arrived"
              options={ARRIVED_OPTIONS}
            />
          </div>
          <div className={styles.filterSelect}>
            <Select
              size="sm"
              value={became}
              onChange={(next) => setFilter({ became: next })}
              ariaLabel="Filter by what it became"
              options={BECAME_OPTIONS}
            />
          </div>
          {/* Full-width on mobile so the two dates share the row and
              shrink rather than spilling past it. */}
          <div className={styles.dateRange}>
            <DateInputNZ
              value={from}
              onChange={(next) => setFilter({ from: next })}
              ariaLabel="Filter from date"
              className={styles.dateField}
            />
            <span className={styles.dateSeparator}>to</span>
            <DateInputNZ
              value={to}
              min={from || undefined}
              onChange={(next) => setFilter({ to: next })}
              ariaLabel="Filter to date"
              className={styles.dateField}
            />
          </div>
          {(from || to) && (
            <Button
              variant="link"
              onClick={() => setFilter({ from: "", to: "" })}
            >
              Clear dates
            </Button>
          )}
          {selected.size > 0 && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                setDeleteError(null);
                setDeleteOpen(true);
              }}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : `Delete (${selected.size})`}
            </Button>
          )}
        </div>

        {!arrivals ? (
          <p className={styles.loading}>Loading…</p>
        ) : arrivals.length === 0 ? (
          <p className={styles.empty}>
            {emptyMessage(filtered, page)}
          </p>
        ) : (
          // scroll="x-auto" is DESIGN.md's wide-table exception: the rows
          // are plain links with no in-cell dropdown, so nothing can be
          // clipped by the scroll container.
          <ResponsiveTable breakpoint="md" scroll="x-auto">
            <TableHead>
              {/* Empty header keeps the desktop checkbox column aligned;
                  the checkboxes and Delete (N) are the whole affordance. */}
              <TableHeaderCell
                padding={styles.headCell}
                className={styles.selectColumn}
              />
              <TableHeaderCell padding={styles.headCell}>Time</TableHeaderCell>
              <TableHeaderCell padding={styles.headCell}>Who</TableHeaderCell>
              <TableHeaderCell padding={styles.headCell}>About</TableHeaderCell>
              <TableHeaderCell padding={styles.headCell}>
                Arrived
              </TableHeaderCell>
            </TableHead>
            <TableBody>
              {arrivals.map((arrival) => (
                <ArrivalRow
                  key={rowKey(arrival)}
                  arrival={arrival}
                  from={query}
                  selected={selected.has(rowKey(arrival))}
                  onToggle={toggleSelected}
                />
              ))}
            </TableBody>
          </ResponsiveTable>
        )}

        {arrivals && total > 0 && (
          <div className={styles.pager}>
            <p className={styles.pagerCount}>
              {first === 0 ? `${total} in total` : `${first}–${last} of ${total}`}
            </p>
            <div className={styles.pagerControls}>
              <button
                type="button"
                aria-label="Newer"
                disabled={page === 1}
                onClick={() => setFilter({ page: String(page - 1) })}
                className={styles.pagerButton}
              >
                <ChevronLeftIcon className={styles.pagerIcon} />
              </button>
              <span>
                Page {page} of {pages}
              </span>
              <button
                type="button"
                aria-label="Older"
                disabled={page >= pages}
                onClick={() => setFilter({ page: String(page + 1) })}
                className={styles.pagerButton}
              >
                <ChevronRightIcon className={styles.pagerIcon} />
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        open={deleteOpen}
        title={`Delete ${selected.size} ${selected.size === 1 ? "enquiry" : "enquiries"}?`}
        message="A call's recording and transcript go with it. This cannot be undone."
        error={deleteError}
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => {
          setDeleteOpen(false);
          setDeleteError(null);
        }}
      />
    </div>
  );
}

/**
 * One arrival. The whole row is the link, at every width: below `md` it
 * is a card and a card with one tappable word in it is a card nobody
 * taps. The checkbox is the one thing on it that is not the link, and it
 * rides the card's top-right corner below `md` exactly as it does on the
 * RMS's events list.
 *
 * The list the reader came from rides along in the link's state, so the
 * screen it opens can offer the way back to that list rather than to an
 * unfiltered one.
 *
 * A row about a job (#115) is named for the job — the Inquiry's event
 * name, with the day and the venue under it — and says how many calls
 * are about it and when the last one rang, because that is what a
 * person running down the list is looking for. It opens on the job.
 */
function ArrivalRow({
  arrival,
  from,
  selected,
  onToggle,
}: {
  arrival: ListRow;
  from: string;
  selected: boolean;
  onToggle: (key: string) => void;
}) {
  const to = rowPath(arrival);
  const job = Boolean(arrival.backend_ref);
  const when = [formatEventDate(arrival.event_date), arrival.venue]
    .filter(Boolean)
    .join(" · ");
  const calls = callsLine(arrival.calls, arrival.last_at);

  return (
    <TableRow columns={4}>
      <TableCell
        corner
        padding={styles.cornerCell}
        className={styles.selectCell}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          aria-label={`Select ${whoLabel(arrival.who, arrival.from_e164)}`}
          checked={selected}
          onChange={() => onToggle(rowKey(arrival))}
          className={styles.checkbox}
        />
      </TableCell>
      <TableCell
        label="Time"
        padding={styles.cellFlush}
        className={styles.nowrapCell}
      >
        <Link to={to} state={{ from }} className={styles.cellLink}>
          <span className={styles.whenText}>{formatWhen(arrival.at)}</span>
        </Link>
      </TableCell>
      <TableCell label="Who" padding={styles.cellFlush}>
        <Link to={to} state={{ from }} className={styles.cellLink}>
          <span className={styles.whoName}>
            {whoLabel(arrival.who, arrival.from_e164)}
          </span>
          {/* Under the name, where there is one: where they rang from,
              and the number — it is what somebody rings back on. */}
          {arrival.who && (arrival.who_org || arrival.from_e164) && (
            <span className={styles.rowMeta}>
              {[arrival.who_org, arrival.from_e164].filter(Boolean).join(" · ")}
            </span>
          )}
        </Link>
      </TableCell>
      {/* What the call was about, in its own column beside who rang:
          its summary cut short (#119), or what the job is (#115) where
          the row is one. Empty on a form submission about no job and on
          a call nobody could summarise; the whole of what was said is
          read on the arrival itself. */}
      <TableCell label="About" span={4} padding={styles.cellFlush}>
        <Link to={to} state={{ from }} className={styles.cellLink}>
          <span className={styles.aboutText}>
            {job ? arrival.event_name || arrival.summary : arrival.summary}
          </span>
          {job && when && <span className={styles.rowMeta}>{when}</span>}
          {calls && <span className={styles.rowMeta}>{calls}</span>}
        </Link>
      </TableCell>
      <TableCell
        label="Arrived"
        padding={styles.cellFlush}
        className={styles.nowrapCell}
      >
        <Link to={to} state={{ from }} className={styles.cellLink}>
          <span className={arrivedBadgeClass(arrival.arrived)}>
            {arrivedLabel(arrival.arrived)}
          </span>
        </Link>
      </TableCell>
    </TableRow>
  );
}

/**
 * What an empty list says. A Company with no calls and a filter that
 * matched none are different facts, and only one of them is about the
 * Company's phone.
 */
function emptyMessage(filtered: boolean, page: number): string {
  if (filtered) return "Nothing matches that.";
  return page === 1
    ? "Nothing has come in yet."
    : "Nothing on this page. Go back to the newer arrivals.";
}
